import "videojs-contrib-ads";
import "videojs-contrib-ads/dist/videojs-contrib-ads.css";
import "videojs-ima";
import "videojs-ima/dist/videojs.ima.css";
import { ADS_ROLL_TYPE, ADS_WATCHED, NEED_GDPR_CONTROL } from "@constants";
import { canAutoplayWithSound } from "../../lib/autoplay";
import i18n from "../../core/i18n";
import { getAdLabel } from "./AdsAutopromotions";
import { renderMidRollMarkers } from "./AdsMidRollMarkers";
import { AdsPrerollLoader } from "./AdsPrerollLoader";
import { findMidRollAfterSeek, findMidRollCrossed, findMidRollToPrefetch, midRollMarkerPositions, } from "./midRollSchedule";
import { prerollStepTarget, shouldHoldVodContent } from "./prerollProgress";
/**
 * Fasi del break pubblicitario.
 * Ho messo tutto in un'unica macchina a stati perche' prima pre/mid/post-roll, autopromo e CUE
 * si muovevano ognuno per conto suo e si pestavano i piedi (richieste doppie, VOD riportato a 0,
 * live fermata durante l'ad).
 */
const BREAK_PHASE = {
    /** nessun break e nessuna richiesta in corso */
    IDLE: 0,
    /** waterfall VAST in corso */
    LOADING: 1,
    /** ad (o autopromo) pronta, aspetta il momento giusto per partire */
    READY: 2,
    /** ho chiesto a IMA/autopromo di partire, aspetto l'inizio effettivo */
    STARTING: 3,
    /** break in riproduzione */
    PLAYING: 4,
};
// Secondi di anticipo con cui richiedo in background mid-roll e post-roll del VOD: abbastanza per
// far girare il waterfall, ma non cosi' tanti da usare una risposta VAST ormai scaduta
const PREFETCH_SECONDS = 15;
// Se IMA (o l'autopromo) non parte entro questo tempo dal comando di play considero il VAST fallito
const BREAK_START_TIMEOUT_MS = 10000;
// Durante un break live (CUE / websocket) con waterfall vuoto riprovo dopo questo tempo
const LIVE_RETRY_DELAY_MS = 10000;
// Oltre questa distanza (secondi) dal punto atteso riallineo la live dopo un break
const LIVE_RESYNC_THRESHOLD_SECONDS = 3;
// Durata della lenta avanzata dell'anello mentre si aspetta la risposta di un VAST, e dello
// scatto finale quando l'ad e' pronta
const LOADER_CREEP_MS = 6000;
const LOADER_DONE_MS = 350;
const AD_CUES_TRACK_LABEL = "ad-cues";
/**
 * Break controller per Video.js + IMA.
 *
 * Regole:
 * - niente ads prima del primo gesto dell'utente (il player non ha autoplay);
 * - il loader grafico si vede solo durante il waterfall del pre-roll, mid/post-roll lavorano in background;
 * - durante il break il VOD resta in pausa, la live continua sotto in muto;
 * - alla fine del pre-roll il contenuto riparte sempre con l'audio;
 * - se il waterfall e' vuoto parte l'autopromo, con le stesse regole;
 * - mid-roll live con is_ads_playing: decidono i CUE HLS, il websocket e' solo fallback.
 */
export class AdsVideoJs {
    constructor(adsClass) {
        this.imaAvailable = false;
        this.phase = BREAK_PHASE.IDLE;
        this.rollType = null;
        this.waterfall = [];
        this.waterfallIndex = 0;
        this.currentEntry = null;
        this.playWhenReady = false;
        this.autopromoReady = false;
        this.breakStartRegistered = false;
        this.contentStarted = false;
        this.prerollPending = false;
        this.prerollDone = false;
        this.contentEnded = false;
        this.contentFinished = false;
        // audio da ripristinare quando il contenuto torna visibile
        this.audioBeforeBreak = null;
        this.adVolume = 1;
        // VOD: mid-roll a tempo e post-roll
        this.midRollTimes = [];
        // midRollUsed[i] diventa true quando l'ad del punto i parte davvero: da li' il punto non riparte
        // piu' fino al refresh. I punti saltati restano false, cosi' tornandoci sopra partono
        this.midRollUsed = [];
        // posizione dell'ultimo controllo: senza salti parte il punto attraversato da li' ad adesso
        this.lastContentTime = 0;
        // c'e' stato un salto non ancora valutato: lo valuto quando la riproduzione riprende
        this.seekPending = false;
        // punto per cui ho chiesto l'ad (-1 nessuno): si consuma solo quando l'ad parte
        this.pendingMidRollIndex = -1;
        this.prefetchedMidRollIndex = -1;
        this.postRollPrefetched = false;
        // live
        this.liveAdsPlaying = false;
        this.cueTagsDetected = false;
        this.cueTrack = null;
        this.liveWatchedSeconds = 0;
        this.liveTemporizedInterval = null;
        this.liveRetryTimeout = null;
        this.liveDelayBeforeBreak = null;
        this.breakStartTimeout = null;
        this.loader = null;
        this.cleanups = [];
        this.destroyed = false;
        this.onContentPlay = () => {
            if (this.destroyed) {
                return;
            }
            if (this.contentStarted) {
                // Il VOD deve restare fermo finche' il pre-roll non e' finito (contrib-ads rilancia un play)
                // e per tutta la durata di un break (es. autopromo mentre l'utente rilascia la barra)
                this.holdVodIfNeeded();
                return;
            }
            // Il player non ha autoplay: questo primo play arriva da un tap dell'utente oppure da
            // autostartIfAllowed, quando il browser conferma che l'utente ha gia' interagito col sito
            this.contentStarted = true;
            this.onUserGesture();
            if (!this.hasPreroll()) {
                this.prerollDone = true;
                this.afterPreroll();
                return;
            }
            this.prerollPending = true;
            this.audioBeforeBreak = this.captureAudio();
            this.adVolume = this.audioToAdVolume(this.audioBeforeBreak);
            if (this.isLive) {
                // la live scorre sotto il loader, muta
                this.player.muted(true);
            }
            else {
                // il VOD resta fermo per tutto il waterfall
                this.player.pause();
                // Il VOD non e' mai partito, quindi contrib-ads non riceve "playing" e resta nello stato
                // Preroll "contenuto in ripresa": li' a ogni VAST fallito del waterfall (adserror) richiama
                // player.play() e il VOD andava avanti con l'audio sotto il loader. Con contentresumed
                // passa a ContentPlayback, dove adserror non fa nulla: il contenuto lo gestisco io
                this.player.trigger("contentresumed");
            }
            this.showLoader();
            this.startRoll(ADS_ROLL_TYPE.PRE_ROLL, true);
        };
        /**
         * Annulla un play del VOD arrivato durante il waterfall del pre-roll o durante un break: per
         * esempio quando l'utente rilascia la barra dopo un salto e video.js rifa' play().
         * La live non la fermo mai: continua muta sotto l'ad.
         */
        this.holdVodIfNeeded = () => {
            if (this.destroyed) {
                return;
            }
            const hold = shouldHoldVodContent({
                isLive: this.isLive,
                prerollPending: this.prerollPending,
                breakActive: this.isBreakActive(),
            });
            if (hold) {
                this.player.pause();
            }
        };
        this.onTimeUpdate = () => {
            if (this.destroyed || this.isLive || !this.prerollDone) {
                return;
            }
            if (this.isBreakActive() || this.contentEnded) {
                return;
            }
            // Durante il trascinamento video.js mette in pausa e fa un seek a ogni movimento del mouse:
            // valuto solo con il video in riproduzione, cosi' l'ad non parte mentre l'utente cerca il
            // punto (19.3.4). Anche dopo un salto a video fermo aspetto il Play
            if (this.player.paused() || this.player.scrubbing?.()) {
                return;
            }
            const currentTime = Number(this.player.currentTime());
            if (!Number.isFinite(currentTime)) {
                return;
            }
            this.checkVodMidRoll(currentTime);
            this.checkVodPostRollPrefetch(currentTime);
        };
        this.onContentSeeking = () => {
            // In STARTING il VOD scorre ancora finche' IMA non chiede la pausa: quel salto lo valuto a
            // fine break. Durante l'ad invece contrib-ads rinomina i seek in adseeking
            if (this.destroyed || this.isLive || this.phase === BREAK_PHASE.PLAYING) {
                return;
            }
            this.seekPending = true;
            this.prefetchedMidRollIndex = -1;
            // Un'ad chiesta prima del salto vale per una posizione che l'utente sta lasciando: il
            // waterfall in corso resta come prefetch ma non parte piu' da solo. Lo tolgo subito e non al
            // timeupdate dopo, perche' l'ad puo' diventare pronta durante il trascinamento o mentre HLS
            // carica il punto del salto. Se l'ad sta gia' partendo (STARTING) la lascio andare
            if (this.rollType === ADS_ROLL_TYPE.MID_ROLL &&
                (this.phase === BREAK_PHASE.LOADING || this.phase === BREAK_PHASE.READY)) {
                this.playWhenReady = false;
                this.pendingMidRollIndex = -1;
            }
        };
        this.onContentEnded = () => {
            if (this.destroyed || this.isLive || this.contentEnded) {
                return;
            }
            // contentended arriva anche se l'utente salta alla fine durante il caricamento del pre-roll
            if (this.isBreakActive()) {
                return;
            }
            this.contentEnded = true;
            this.adsClass.videoEnded = true;
            if (!this.hasPostRoll()) {
                this.finishContent();
                return;
            }
            // un mid-roll caricato ma mai partito non serve piu'
            if (this.rollType !== ADS_ROLL_TYPE.POST_ROLL) {
                this.cancelPendingRoll();
            }
            this.audioBeforeBreak = this.captureAudio();
            this.adVolume = this.audioToAdVolume(this.audioBeforeBreak);
            if (this.phase === BREAK_PHASE.READY) {
                this.playLoadedBreak();
            }
            else if (this.phase === BREAK_PHASE.LOADING) {
                this.playWhenReady = true;
            }
            else {
                this.startRoll(ADS_ROLL_TYPE.POST_ROLL, true);
            }
        };
        this.onImaEvent = (event) => {
            if (this.destroyed) {
                return;
            }
            // utile per la gestione pubblicita' coin in coinShop.js
            window.dispatchEvent(new CustomEvent("ads_player_event", {
                detail: {
                    type: event.type,
                    originalEvent: event,
                    message: "Ad event: " + event.type,
                },
            }));
            const eventTypes = google.ima.AdEvent.Type;
            switch (event.type) {
                case eventTypes.LOADED:
                    clearTimeout(this.adsClass.firstAdFailedTimer);
                    break;
                case eventTypes.CONTENT_PAUSE_REQUESTED:
                    this.onBreakStart();
                    break;
                case eventTypes.STARTED:
                    this.onImaAdStarted();
                    break;
                case eventTypes.FIRST_QUARTILE:
                    this.adsClass.isFirstAdFailed = false;
                    this.adsClass.setAdEventOnDB(ADS_WATCHED);
                    this.adsClass.setAdAllEventOnDB(ADS_WATCHED);
                    break;
                case eventTypes.CONTENT_RESUME_REQUESTED:
                    this.onBreakEnd();
                    break;
                case eventTypes.ALL_ADS_COMPLETED:
                    // di norma CONTENT_RESUME_REQUESTED e' gia' arrivato: questo e' solo un paracadute
                    if (this.isBreakActive()) {
                        this.onBreakEnd();
                    }
                    break;
            }
        };
        this.onAdError = (event) => {
            if (this.destroyed) {
                return;
            }
            const adError = event?.data?.AdError;
            const errorCode = adError?.getErrorCode?.() ?? adError?.data?.errorCode ?? "n/d";
            console.warn("[Ads] Errore ad (" + errorCode + ") roll " + this.rollType, this.adsClass.actualTagUrl);
            // Errore di una richiesta vecchia arrivato dopo l'annullamento del break
            if (this.phase === BREAK_PHASE.IDLE) {
                return;
            }
            this.adsClass.failedAdsSaveOnDb();
            if (this.phase === BREAK_PHASE.PLAYING) {
                // L'ad si e' rotta a meta': chiudo il break e torno al contenuto
                this.onBreakEnd();
                return;
            }
            // Qualunque codice di errore fa avanzare il waterfall (prima solo 5 codici lo facevano)
            if (this.phase === BREAK_PHASE.STARTING) {
                this.clearBreakStartTimeout();
                this.playWhenReady = true;
            }
            this.phase = BREAK_PHASE.LOADING;
            this.requestNextVast();
        };
        this.adsClass = adsClass;
        this.player = adsClass.videoPlayer;
        this.isLive = adsClass.isLive == true;
        this.midRollTimes = Array.isArray(adsClass.midRollTimes)
            ? adsClass.midRollTimes
            : [];
        this.midRollUsed = this.midRollTimes.map(() => false);
        this.adsClass.cycleAndCompileAllTagVast();
        this.createLoader();
        this.setupIma();
        this.bindPlayerEvents();
        if (this.isLive && this.adsClass.adsCustomizationTabular == true) {
            // Il listener sui CUE parte subito: anche prima del primo play mi serve sapere
            // se il manifest ha i cue tag, cosi' il websocket resta un fallback
            this.setupCueListener();
        }
        this.player.ready(() => this.onPlayerReady());
    }
    // ==========================================================
    // API USATA DA Ads / VideoPlayer
    // ==========================================================
    /**
     * True mentre un'ad o un'autopromo e' in riproduzione (o sta partendo).
     * VideoPlayer la usa per non toccare mai la src del contenuto durante un break.
     */
    isBreakActive() {
        return (this.phase === BREAK_PHASE.STARTING || this.phase === BREAK_PHASE.PLAYING);
    }
    /**
     * Stato is_ads_playing della live (CUE-OUT/CUE-IN oppure websocket).
     * I CUE hanno la precedenza: appena ne vedo uno nel manifest il websocket viene ignorato.
     */
    setLiveAdsPlaying(active, source) {
        if (this.destroyed || !this.isLive) {
            return;
        }
        if (!this.adsClass.adsCustomizationTabular) {
            return;
        }
        if (source === "socket" && this.cueTagsDetected) {
            return;
        }
        if (this.liveAdsPlaying === active) {
            return;
        }
        this.liveAdsPlaying = active;
        this.adsClass.isAdsPlayingStatus = active;
        // Prima del pre-roll non faccio nulla: a fine pre-roll ricontrollo lo stato
        if (!this.prerollDone) {
            return;
        }
        if (active) {
            this.startLiveTabularBreak();
        }
        else {
            this.stopLiveTabularBreak();
        }
    }
    destroy() {
        if (this.destroyed) {
            return;
        }
        this.destroyed = true;
        this.clearBreakStartTimeout();
        this.clearLiveRetry();
        if (this.liveTemporizedInterval !== null) {
            clearInterval(this.liveTemporizedInterval);
            this.liveTemporizedInterval = null;
        }
        this.cleanups.forEach((cleanup) => {
            try {
                cleanup();
            }
            catch {
                // il player puo' essere gia' stato distrutto
            }
        });
        this.cleanups = [];
        this.adsClass.autoPromotionManager?.destroy?.();
        try {
            this.player?.ima?.getAdsManager?.()?.destroy?.();
        }
        catch {
            // adsManager gia' distrutto
        }
        this.loader?.destroy();
        this.loader = null;
    }
    // ==========================================================
    // SETUP
    // ==========================================================
    setupIma() {
        if (typeof google === "undefined" || !google?.ima) {
            // Senza SDK IMA (tipicamente bloccato da un adblock) non posso erogare nulla
            console.warn("[Ads] SDK IMA non disponibile");
            this.adsClass.onAdBlockDetected?.();
            return;
        }
        this.imaAvailable = true;
        this.player.ima({
            // i break li faccio partire io con playAdBreak(), quando serve
            autoPlayAdBreaks: false,
            debug: false,
            // su iOS l'ad usa un <video> suo: cosi' la live puo' continuare sotto
            disableCustomPlaybackForIOS10Plus: true,
            // stessa etichetta del conto alla rovescia dell'autopromo
            adLabel: getAdLabel(),
            adLabelNofN: i18n.t("advertisement-of", "di"),
            adsManagerLoadedCallback: () => this.onAdsManagerLoaded(),
        });
        google.ima.settings.setVpaidMode(google.ima.ImaSdkSettings.VpaidMode.INSECURE);
        // contrib-ads non deve aprire break da solo. Se si aspetta un pre-roll, a ogni VAST fallito del
        // waterfall riceve un adserror, pensa che il pre-roll non ci sia e fa partire il contenuto.
        // Pre, mid e post-roll li apro io con playAdBreak()
        this.player.trigger("nopreroll");
        this.player.trigger("nopostroll");
        this.listenPlayer("adserror", this.onAdError);
    }
    bindPlayerEvents() {
        const playerElement = this.player.el?.() ?? null;
        if (playerElement) {
            // IMA su mobile vuole l'inizializzazione dell'ad display container dentro un gesto dell'utente:
            // uso la fase di capture cosi' passo prima del click handler di video.js
            const onGesture = () => this.onUserGesture();
            const gestureEvents = ["click", "touchend", "keydown"];
            gestureEvents.forEach((type) => playerElement.addEventListener(type, onGesture, true));
            this.cleanups.push(() => gestureEvents.forEach((type) => playerElement.removeEventListener(type, onGesture, true)));
        }
        this.listenPlayer("play", this.onContentPlay);
        // contrib-ads rinomina il play del contenuto in adplay durante un break e in contentplay
        // mentre il contenuto "riprende": anche quei play non devono far partire il VOD
        this.listenPlayer("adplay", this.holdVodIfNeeded);
        this.listenPlayer("contentplay", this.holdVodIfNeeded);
        // Il controllo dei mid-roll VOD gira su timeupdate, e solo con il video in riproduzione.
        // contrib-ads, mentre il contenuto riprende dopo un break, rinomina gli eventi con il prefisso
        // "content": li ascolto entrambi per non perdere nessun passaggio.
        // Prima ascoltavo anche seeked: l'ad partiva durante il trascinamento o a video fermo
        ["timeupdate", "contenttimeupdate"].forEach((type) => this.listenPlayer(type, this.onTimeUpdate));
        // Un salto (barra, pulsanti +-5 s, tastiera) lo segno soltanto: lo valuto alla ripresa
        ["seeking", "contentseeking"].forEach((type) => this.listenPlayer(type, this.onContentSeeking));
        // la durata arriva con i metadati (e con HLS puo' cambiare): ridisegno le barrette dei mid-roll
        this.listenPlayer("durationchange", () => this.updateMidRollMarkers());
        // con nopostroll contrib-ads emette contentended e poi ended: li ascolto entrambi
        this.listenPlayer("contentended", this.onContentEnded);
        this.listenPlayer("ended", this.onContentEnded);
        this.listenPlayer("dispose", () => this.destroy());
    }
    listenPlayer(type, handler) {
        this.player.on(type, handler);
        this.cleanups.push(() => this.player.off(type, handler));
    }
    createLoader() {
        const playerElement = this.player.el?.() ?? null;
        if (!playerElement) {
            return;
        }
        // copertina (poster) e titolo arrivano da VideoPlayer nelle opzioni di video.js
        const contentTitle = String(this.player.options?.()?.contentTitle ?? "").trim();
        this.loader = new AdsPrerollLoader(playerElement, {
            posterUrl: this.player.poster?.() || null,
            title: i18n.t("ad-loading-title", "La pubblicità sta per iniziare"),
            subtitle: contentTitle
                ? i18n.t("ad-loading-next", {
                    defaultValue: "Poi parte {{title}}",
                    title: contentTitle,
                })
                : null,
        });
    }
    onPlayerReady() {
        if (this.destroyed || this.openGdprModalIfNeeded()) {
            return;
        }
        void this.autostartIfAllowed();
    }
    /**
     * Se l'utente ha gia' interagito con il sito (es. click su un contenuto della home o della barra
     * laterale) il browser permette di partire con l'audio: parto subito come dopo un tap, quindi
     * pre-roll (se c'e') e poi contenuto con l'audio. Altrimenti resta il big play e si aspetta il tap.
     */
    async autostartIfAllowed() {
        const allowed = await canAutoplayWithSound();
        if (!allowed || this.destroyed || this.contentStarted) {
            return;
        }
        this.onUserGesture();
        this.playContent();
    }
    /**
     * Con NEED_GDPR_CONTROL attivo, se l'utente non ha ancora dato il consenso apro il modal del CMP
     * (come faceva il legacy all'avvio del player)
     * @returns true se il consenso manca: in quel caso il player aspetta l'utente
     */
    openGdprModalIfNeeded() {
        if (!NEED_GDPR_CONTROL) {
            return false;
        }
        if (typeof tcStringGDPR !== "undefined" && tcStringGDPR) {
            return false;
        }
        if ($("#qc-cmp2-ui").is(":hidden")) {
            $("#qc-cmp2-persistent-link").trigger("click");
        }
        return true;
    }
    setupCueListener() {
        const textTracks = this.player.textTracks?.();
        if (!textTracks) {
            return;
        }
        const attach = (track) => {
            if (!track || track.label !== AD_CUES_TRACK_LABEL || this.cueTrack) {
                return;
            }
            this.cueTrack = track;
            // con mode "disabled" activeCues resta vuoto e cuechange non arriva
            track.mode = "hidden";
            const onCueChange = () => this.onCueChange();
            track.addEventListener("cuechange", onCueChange);
            this.cleanups.push(() => track.removeEventListener("cuechange", onCueChange));
            this.onCueChange();
        };
        for (let i = 0; i < textTracks.length; i++) {
            attach(textTracks[i]);
        }
        const onAddTrack = (event) => attach(event.track);
        textTracks.addEventListener("addtrack", onAddTrack);
        this.cleanups.push(() => textTracks.removeEventListener("addtrack", onAddTrack));
    }
    // ==========================================================
    // EVENTI PLAYER
    // ==========================================================
    onUserGesture() {
        if (!this.imaAvailable) {
            return;
        }
        try {
            // videojs-ima lo inizializza una volta sola, le chiamate successive non fanno nulla
            this.player.ima.initializeAdDisplayContainer();
        }
        catch (error) {
            console.warn("[Ads] initializeAdDisplayContainer fallito:", error);
        }
    }
    onCueChange() {
        const track = this.cueTrack;
        if (!track) {
            return;
        }
        if (track.cues && track.cues.length > 0) {
            this.markCueTagsDetected();
        }
        const active = !!(track.activeCues && track.activeCues.length > 0);
        this.setLiveAdsPlaying(active, "cue");
    }
    markCueTagsDetected() {
        if (this.cueTagsDetected) {
            return;
        }
        // Il manifest ha i cue tag: da qui in poi decidono solo loro e il websocket non serve piu'
        this.cueTagsDetected = true;
        this.adsClass.stopAdsSocket?.();
    }
    // ==========================================================
    // WATERFALL
    // ==========================================================
    hasPreroll() {
        return (Number(this.adsClass.adsCustomizationPreRoll) === 1 &&
            (this.adsClass.tagUrl.pre_roll.length > 0 || this.hasAutopromotions()));
    }
    hasPostRoll() {
        return (!this.isLive &&
            Number(this.adsClass.adsCustomizationPostRoll) === 1 &&
            (this.adsClass.tagUrl.post_roll.length > 0 || this.hasAutopromotions()));
    }
    hasVodMidRoll() {
        return (!this.isLive &&
            Number(this.adsClass.adsCustomizationMidRollType) > 0 &&
            this.midRollTimes.length > 0 &&
            (this.adsClass.tagUrl.mid_roll.length > 0 || this.hasAutopromotions()));
    }
    hasAutopromotions() {
        return !!this.adsClass.autoPromotionManager?.hasAutopromotions?.();
    }
    startRoll(rollType, playWhenReady) {
        if (this.destroyed) {
            return;
        }
        this.rollType = rollType;
        this.playWhenReady = playWhenReady;
        this.autopromoReady = false;
        this.breakStartRegistered = false;
        this.currentEntry = null;
        this.phase = BREAK_PHASE.LOADING;
        this.waterfall = this.imaAvailable
            ? this.adsClass.buildWaterfall(rollType)
            : [];
        this.waterfallIndex = 0;
        this.requestNextVast();
    }
    requestNextVast() {
        if (this.destroyed || this.phase !== BREAK_PHASE.LOADING) {
            return;
        }
        if (this.waterfallIndex >= this.waterfall.length) {
            this.onWaterfallExhausted();
            return;
        }
        const entry = this.waterfall[this.waterfallIndex];
        this.waterfallIndex++;
        this.currentEntry = entry;
        this.updateLoaderProgress();
        const tagUrl = this.adsClass.prepareVastRequest(this.rollType, entry);
        if (!tagUrl) {
            this.requestNextVast();
            return;
        }
        try {
            // changeAdTag + requestAds e NON setContentWithAdTag: quest'ultimo riporta il contenuto
            // a currentTime(0) e lo mette in pausa a ogni richiesta (videojs-ima changeSource)
            this.player.ima.changeAdTag(tagUrl);
            this.player.ima.requestAds();
        }
        catch (error) {
            console.error("[Ads] Errore richiesta VAST:", error);
            this.adsClass.failedAdsSaveOnDb();
            this.requestNextVast();
        }
    }
    onWaterfallExhausted() {
        const rollType = this.rollType;
        this.adsClass.onWaterfallExhausted(rollType);
        if (this.hasAutopromotions()) {
            this.autopromoReady = true;
            this.phase = BREAK_PHASE.READY;
            this.completeLoaderProgress();
            if (this.playWhenReady) {
                this.playLoadedBreak();
            }
            return;
        }
        this.resetRoll();
        // Nessun break partira': l'audio salvato per un mid/post-roll non serve piu'
        // (durante una pausa live CUE invece lo tengo, serve al ritorno sulla live)
        if (rollType !== ADS_ROLL_TYPE.PRE_ROLL && !this.liveAdsPlaying) {
            this.audioBeforeBreak = null;
        }
        if (rollType === ADS_ROLL_TYPE.PRE_ROLL) {
            // Nessun pre-roll disponibile: si entra subito nel contenuto, con l'audio
            this.finishPreroll();
        }
        else if (rollType === ADS_ROLL_TYPE.POST_ROLL && this.contentEnded) {
            this.finishContent();
        }
        else if (this.isLive && this.liveAdsPlaying) {
            this.scheduleLiveRetry();
        }
        else if (this.isLive) {
            this.liveWatchedSeconds = 0;
        }
    }
    onAdsManagerLoaded() {
        if (this.destroyed) {
            return;
        }
        const eventTypes = google.ima.AdEvent.Type;
        const events = [
            eventTypes.LOADED,
            eventTypes.STARTED,
            eventTypes.FIRST_QUARTILE,
            eventTypes.MIDPOINT,
            eventTypes.THIRD_QUARTILE,
            eventTypes.COMPLETE,
            eventTypes.SKIPPED,
            eventTypes.CLICK,
            eventTypes.PAUSED,
            eventTypes.RESUMED,
            eventTypes.CONTENT_PAUSE_REQUESTED,
            eventTypes.CONTENT_RESUME_REQUESTED,
            eventTypes.ALL_ADS_COMPLETED,
        ];
        // Ogni requestAds crea un nuovo adsManager: i listener vanno registrati ogni volta.
        // AD_ERROR non c'e' apposta: gli errori arrivano gia' come adserror sul player e gestirli
        // due volte faceva saltare un VAST del waterfall
        events.forEach((type) => this.player.ima.addEventListener(type, this.onImaEvent));
        // Risposta arrivata dopo che il break e' stato annullato (es. CUE-IN): la ignoro
        if (this.phase !== BREAK_PHASE.LOADING) {
            return;
        }
        // videojs-ima in modalita' manuale ha gia' fatto adsManager.init(): l'ad si puo' avviare
        this.phase = BREAK_PHASE.READY;
        this.completeLoaderProgress();
        if (this.playWhenReady) {
            this.playLoadedBreak();
        }
    }
    // ==========================================================
    // BREAK
    // ==========================================================
    playLoadedBreak() {
        if (this.destroyed || this.phase !== BREAK_PHASE.READY) {
            this.playWhenReady = true;
            return;
        }
        // Mid-roll del VOD pronto mentre l'utente ha messo in pausa o sta trascinando il cursore:
        // aspetto la ripresa, poi lo fa partire checkVodMidRoll (19.3.4)
        if (!this.isLive &&
            this.rollType === ADS_ROLL_TYPE.MID_ROLL &&
            (this.player.paused() || this.player.scrubbing?.())) {
            this.playWhenReady = true;
            return;
        }
        if (this.autopromoReady) {
            this.playAutopromo();
            return;
        }
        this.phase = BREAK_PHASE.STARTING;
        this.armBreakStartTimeout();
        try {
            this.player.ima.playAdBreak();
        }
        catch (error) {
            console.error("[Ads] playAdBreak fallito:", error);
            this.clearBreakStartTimeout();
            this.adsClass.failedAdsSaveOnDb();
            this.phase = BREAK_PHASE.LOADING;
            this.playWhenReady = true;
            this.requestNextVast();
        }
    }
    playAutopromo() {
        this.autopromoReady = false;
        this.phase = BREAK_PHASE.STARTING;
        // Stesse regole di un'ad VAST: VOD fermo subito, live che continua muta sotto
        this.applyContentRulesForBreak();
        this.adsClass.autoPromotionManager.play(this.player, {
            volume: this.adVolume,
            onStart: () => {
                this.hideLoader();
                this.onBreakStart();
                this.consumeMidRoll();
            },
            onEnd: () => this.onBreakEnd(),
            onError: () => this.onBreakEnd(),
        });
    }
    onBreakStart() {
        if (this.destroyed) {
            return;
        }
        this.clearBreakStartTimeout();
        this.phase = BREAK_PHASE.PLAYING;
        this.adsClass.isAdvStarted = true;
        if (this.isLive) {
            this.liveDelayBeforeBreak = this.getLiveDelay();
        }
        this.setLanner(false);
        this.applyContentRulesForBreak();
    }
    onImaAdStarted() {
        this.hideLoader();
        // la filler gif serve solo mentre aspetto l'ad: ora c'e' l'ad sopra
        if (this.isLive) {
            this.adsClass.hideFillerGif();
        }
        // videojs-ima allinea il volume dell'ad a quello del player, che per la live e' muto:
        // lo rimetto a quello che l'utente aveva prima del break
        try {
            this.player.ima.getAdsManager()?.setVolume(this.adVolume);
        }
        catch {
            // adsManager non disponibile
        }
        if (!this.breakStartRegistered) {
            this.breakStartRegistered = true;
            clearTimeout(this.adsClass.firstAdFailedTimer);
            this.adsClass.hasStartedAdsOnce = true;
            this.adsClass.onWaterfallSuccess(this.rollType, this.currentEntry);
            this.consumeMidRoll();
        }
    }
    /**
     * VOD in pausa per tutta la durata del break; live che continua a scorrere, muta.
     * videojs-ima a inizio break mette sempre in pausa il player: per la live lo faccio ripartire.
     */
    applyContentRulesForBreak() {
        if (this.isLive) {
            this.player.muted(true);
            if (this.player.paused()) {
                this.playContent();
            }
        }
        else if (!this.player.paused()) {
            this.player.pause();
        }
    }
    onBreakEnd() {
        if (this.destroyed) {
            return;
        }
        if (this.phase !== BREAK_PHASE.PLAYING &&
            this.phase !== BREAK_PHASE.STARTING) {
            return;
        }
        const rollType = this.rollType;
        // false se il break si e' chiuso prima di partire (es. autopromo con mp4 rotto)
        const breakPlayed = this.phase === BREAK_PHASE.PLAYING;
        this.clearBreakStartTimeout();
        this.hideLoader();
        this.resetRoll();
        this.adsClass.isAdvStarted = false;
        if (rollType === ADS_ROLL_TYPE.PRE_ROLL) {
            this.finishPreroll();
            return;
        }
        if (rollType === ADS_ROLL_TYPE.POST_ROLL || this.contentEnded) {
            this.restoreAudio(false);
            this.finishContent();
            return;
        }
        if (this.isLive) {
            if (this.adsClass.adsCustomizationTabular && this.liveAdsPlaying) {
                if (breakPlayed) {
                    // CUE ancora attivo: filler sopra la live (muta) e concateno la prossima ad
                    this.continueLiveTabularBreak();
                }
                else {
                    // il break non e' nemmeno partito: riprovo piu' tardi invece di martellare i VAST
                    this.coverLiveWithFiller();
                    this.scheduleLiveRetry();
                }
                return;
            }
            this.liveWatchedSeconds = 0;
            this.returnToLiveContent();
            return;
        }
        this.restoreAudio(false);
        this.setLanner(true);
        this.playContent();
    }
    finishPreroll() {
        this.prerollPending = false;
        this.prerollDone = true;
        this.hideLoader();
        // A fine pre-roll il contenuto deve avere l'audio, anche se prima non lo aveva
        this.restoreAudio(true);
        this.setLanner(true);
        if (this.isLive) {
            this.resyncLive();
        }
        else {
            this.playContent();
        }
        this.afterPreroll();
    }
    afterPreroll() {
        if (!this.isLive) {
            // mid e post-roll del VOD partono da onTimeUpdate / onContentEnded
            return;
        }
        if (this.adsClass.adsCustomizationTabular) {
            if (this.liveAdsPlaying) {
                this.startLiveTabularBreak();
            }
            return;
        }
        if (Number(this.adsClass.adsCustomizationMidRollType) === 1 &&
            this.midRollTimes.length > 0) {
            this.startLiveTemporizedTimer();
        }
    }
    finishContent() {
        if (this.contentFinished) {
            return;
        }
        this.contentFinished = true;
        this.adsClass.videoEnded = true;
        this.setLanner(true);
        this.adsClass.playerObject?.goToNextVideo?.();
    }
    cancelPendingRoll() {
        if (this.phase !== BREAK_PHASE.LOADING &&
            this.phase !== BREAK_PHASE.READY) {
            return;
        }
        // le risposte che arriveranno con phase IDLE vengono ignorate
        this.resetRoll();
    }
    resetRoll() {
        this.phase = BREAK_PHASE.IDLE;
        this.rollType = null;
        this.playWhenReady = false;
        // se l'ad non e' partita il punto resta libero: un salto successivo lo puo' riprovare
        this.pendingMidRollIndex = -1;
        this.autopromoReady = false;
        this.waterfall = [];
        this.waterfallIndex = 0;
    }
    armBreakStartTimeout() {
        this.clearBreakStartTimeout();
        this.breakStartTimeout = window.setTimeout(() => {
            this.breakStartTimeout = null;
            if (this.phase !== BREAK_PHASE.STARTING) {
                return;
            }
            // IMA non ha mai fatto partire l'ad: la tratto come un VAST fallito
            console.warn("[Ads] L'ad non e' partita entro il timeout");
            this.adsClass.failedAdsSaveOnDb();
            this.phase = BREAK_PHASE.LOADING;
            this.playWhenReady = true;
            this.requestNextVast();
        }, BREAK_START_TIMEOUT_MS);
    }
    clearBreakStartTimeout() {
        if (this.breakStartTimeout !== null) {
            clearTimeout(this.breakStartTimeout);
            this.breakStartTimeout = null;
        }
    }
    // ==========================================================
    // VOD: MID-ROLL A TEMPO E POST-ROLL
    // ==========================================================
    /**
     * Regola dei mid-roll a tempo (doc/Fix/2026-10-13-mid-roll-vod-dopo-seek-progetto.md):
     * - senza salti parte il punto che il video attraversa;
     * - dopo un salto parte l'ULTIMO punto superato, solo se la sua ad non e' mai partita;
     * - i punti saltati piu' indietro restano liberi e partono se l'utente ci torna sopra.
     * Gira solo con il video in riproduzione (vedi onTimeUpdate).
     */
    checkVodMidRoll(currentTime) {
        if (!this.hasVodMidRoll()) {
            return;
        }
        // Mid-roll pronto rimasto in attesa perche' l'utente aveva messo in pausa: parte alla ripresa
        if (this.phase === BREAK_PHASE.READY &&
            this.playWhenReady &&
            this.rollType === ADS_ROLL_TYPE.MID_ROLL) {
            this.playLoadedBreak();
            return;
        }
        const afterSeek = this.seekPending;
        const index = afterSeek
            ? findMidRollAfterSeek(this.midRollTimes, this.midRollUsed, currentTime)
            : findMidRollCrossed(this.midRollTimes, this.midRollUsed, this.lastContentTime, currentTime);
        this.seekPending = false;
        this.lastContentTime = currentTime;
        if (index !== -1) {
            this.triggerMidRoll(index, afterSeek, currentTime);
            return;
        }
        // Waterfall in background un po' prima del prossimo punto libero
        const nextIndex = findMidRollToPrefetch(this.midRollTimes, this.midRollUsed, currentTime, PREFETCH_SECONDS);
        if (this.phase === BREAK_PHASE.IDLE &&
            nextIndex !== -1 &&
            nextIndex !== this.prefetchedMidRollIndex) {
            this.prefetchedMidRollIndex = nextIndex;
            this.startRoll(ADS_ROLL_TYPE.MID_ROLL, false);
        }
    }
    triggerMidRoll(index, afterSeek, currentTime) {
        console.info("[Ads] mid-roll VOD: slot " +
            this.midRollTimes[index] +
            "s " +
            (afterSeek ? "dopo un salto" : "raggiunto") +
            " a " +
            Math.floor(currentTime) +
            "s (fase " +
            this.phase +
            ")");
        // Senza salti, se il waterfall di questo punto e' gia' girato in prefetch ed era vuoto non lo
        // rifaccio a pochi secondi dal primo: il punto resta libero e un salto successivo lo riprova.
        // Vale anche se nel frattempo e' partito il prefetch del post-roll (punto negli ultimi 15 s)
        if (!afterSeek &&
            this.prefetchedMidRollIndex === index &&
            (this.phase === BREAK_PHASE.IDLE ||
                this.rollType === ADS_ROLL_TYPE.POST_ROLL)) {
            return;
        }
        // Un post-roll precaricato non va usato come mid-roll
        if (this.rollType === ADS_ROLL_TYPE.POST_ROLL) {
            this.cancelPendingRoll();
            this.postRollPrefetched = false;
        }
        this.pendingMidRollIndex = index;
        this.audioBeforeBreak = this.captureAudio();
        this.adVolume = this.audioToAdVolume(this.audioBeforeBreak);
        if (this.phase === BREAK_PHASE.READY) {
            this.playLoadedBreak();
        }
        else if (this.phase === BREAK_PHASE.LOADING) {
            // l'ad non e' ancora pronta: parte appena lo e', il VOD intanto continua (opzione B)
            this.playWhenReady = true;
        }
        else {
            this.startRoll(ADS_ROLL_TYPE.MID_ROLL, true);
        }
    }
    /**
     * Il punto si consuma solo quando l'ad parte davvero (primo frame IMA o autopromo partita),
     * anche se poi l'utente non la vede fino in fondo (19.3.5). Se il waterfall e' vuoto o l'ad si
     * rompe prima del primo frame il punto resta libero
     */
    consumeMidRoll() {
        if (this.rollType !== ADS_ROLL_TYPE.MID_ROLL ||
            this.pendingMidRollIndex === -1) {
            return;
        }
        this.midRollUsed[this.pendingMidRollIndex] = true;
        this.pendingMidRollIndex = -1;
        this.updateMidRollMarkers();
    }
    updateMidRollMarkers() {
        if (this.destroyed || !this.hasVodMidRoll()) {
            return;
        }
        renderMidRollMarkers(this.player, midRollMarkerPositions(this.midRollTimes, this.midRollUsed, Number(this.player.duration())));
    }
    checkVodPostRollPrefetch(currentTime) {
        if (this.postRollPrefetched || !this.hasPostRoll()) {
            return;
        }
        if (this.phase !== BREAK_PHASE.IDLE) {
            return;
        }
        const duration = Number(this.player.duration());
        if (!Number.isFinite(duration) || duration <= 0) {
            return;
        }
        if (duration - currentTime <= PREFETCH_SECONDS) {
            this.postRollPrefetched = true;
            this.startRoll(ADS_ROLL_TYPE.POST_ROLL, false);
        }
    }
    // ==========================================================
    // LIVE
    // ==========================================================
    startLiveTabularBreak() {
        if (this.destroyed || !this.liveAdsPlaying) {
            return;
        }
        // c'e' gia' un'ad: a fine ad onBreakEnd vede il CUE ancora attivo e concatena
        if (this.isBreakActive()) {
            return;
        }
        this.clearLiveRetry();
        if (this.audioBeforeBreak === null) {
            this.audioBeforeBreak = this.captureAudio();
        }
        this.adVolume = this.audioToAdVolume(this.audioBeforeBreak);
        this.setLanner(false);
        this.coverLiveWithFiller();
        if (this.phase === BREAK_PHASE.READY) {
            this.playLoadedBreak();
        }
        else if (this.phase === BREAK_PHASE.LOADING) {
            this.playWhenReady = true;
        }
        else {
            this.startRoll(ADS_ROLL_TYPE.MID_ROLL, true);
        }
    }
    continueLiveTabularBreak() {
        this.coverLiveWithFiller();
        this.startRoll(ADS_ROLL_TYPE.MID_ROLL, true);
    }
    stopLiveTabularBreak() {
        this.clearLiveRetry();
        // l'ad in corso finisce normalmente, poi onBreakEnd torna alla live
        if (this.isBreakActive()) {
            return;
        }
        this.cancelPendingRoll();
        this.returnToLiveContent();
    }
    coverLiveWithFiller() {
        // durante la pausa pubblicitaria la live resta sotto la filler gif, muta
        this.adsClass.showFillerGif();
        this.player.muted(true);
        if (this.player.paused()) {
            this.playContent();
        }
    }
    returnToLiveContent() {
        this.adsClass.hideFillerGif();
        this.restoreAudio(false);
        this.setLanner(true);
        this.resyncLive();
    }
    scheduleLiveRetry() {
        this.clearLiveRetry();
        this.liveRetryTimeout = window.setTimeout(() => {
            this.liveRetryTimeout = null;
            if (this.liveAdsPlaying && this.phase === BREAK_PHASE.IDLE) {
                this.startRoll(ADS_ROLL_TYPE.MID_ROLL, true);
            }
        }, LIVE_RETRY_DELAY_MS);
    }
    clearLiveRetry() {
        if (this.liveRetryTimeout !== null) {
            clearTimeout(this.liveRetryTimeout);
            this.liveRetryTimeout = null;
        }
    }
    /**
     * Mid-roll live a tempo (type 1): come nel legacy il primo tempo configurato e' l'intervallo,
     * contato solo sui secondi in cui la live e' effettivamente in riproduzione
     */
    startLiveTemporizedTimer() {
        if (this.liveTemporizedInterval !== null) {
            return;
        }
        const interval = this.midRollTimes[0];
        this.liveTemporizedInterval = window.setInterval(() => {
            if (this.destroyed || this.player.paused() || this.isBreakActive()) {
                return;
            }
            this.liveWatchedSeconds++;
            if (this.phase === BREAK_PHASE.IDLE &&
                this.liveWatchedSeconds >= interval - PREFETCH_SECONDS) {
                this.startRoll(ADS_ROLL_TYPE.MID_ROLL, false);
            }
            if (this.liveWatchedSeconds < interval) {
                return;
            }
            this.audioBeforeBreak = this.captureAudio();
            this.adVolume = this.audioToAdVolume(this.audioBeforeBreak);
            if (this.phase === BREAK_PHASE.READY) {
                this.playLoadedBreak();
            }
            else if (this.phase === BREAK_PHASE.LOADING) {
                this.playWhenReady = true;
            }
        }, 1000);
    }
    /**
     * Distanza in secondi dal live edge (0 se non calcolabile)
     */
    getLiveDelay() {
        try {
            const seekable = this.player.seekable();
            if (!seekable || seekable.length === 0) {
                return null;
            }
            return Math.max(0, seekable.end(seekable.length - 1) - this.player.currentTime());
        }
        catch {
            return null;
        }
    }
    /**
     * Dopo un break la live deve trovarsi dove sarebbe stata se avesse continuato a scorrere.
     * Di norma ha davvero continuato sotto l'ad; se il browser l'ha fermata la riallineo al ritardo
     * che l'utente aveva prima del break (live edge se non lo conosco).
     */
    resyncLive() {
        if (this.player.paused()) {
            this.playContent();
        }
        const expectedDelay = this.liveDelayBeforeBreak;
        this.liveDelayBeforeBreak = null;
        const currentDelay = this.getLiveDelay();
        if (currentDelay === null) {
            return;
        }
        if (expectedDelay === null) {
            return;
        }
        if (Math.abs(currentDelay - expectedDelay) > LIVE_RESYNC_THRESHOLD_SECONDS) {
            const seekable = this.player.seekable();
            this.player.currentTime(seekable.end(seekable.length - 1) - expectedDelay);
        }
    }
    // ==========================================================
    // AUDIO / UI
    // ==========================================================
    captureAudio() {
        return {
            muted: !!this.player.muted(),
            volume: Number(this.player.volume()),
        };
    }
    audioToAdVolume(audio) {
        if (audio.muted) {
            return 0;
        }
        return audio.volume > 0 ? audio.volume : 1;
    }
    /**
     * Ripristina l'audio che l'utente aveva prima del break.
     * Con forceAudio (fine pre-roll) il contenuto riparte comunque con l'audio.
     */
    restoreAudio(forceAudio) {
        const saved = this.audioBeforeBreak ?? { muted: false, volume: 1 };
        this.audioBeforeBreak = null;
        const volume = saved.volume > 0 ? saved.volume : 1;
        this.player.volume(volume);
        this.player.muted(forceAudio ? false : saved.muted);
    }
    playContent() {
        const playPromise = this.player.play();
        if (playPromise && typeof playPromise.catch === "function") {
            playPromise.catch((error) => {
                console.warn("[Ads] play del contenuto non riuscito:", error);
            });
        }
    }
    setLanner(on) {
        if (this.adsClass.isLannerOn === on) {
            return;
        }
        this.adsClass.isLannerOn = on;
        window.dispatchEvent(new Event(on ? "lannerOn" : "lannerOff"));
    }
    showLoader() {
        this.loader?.show();
    }
    hideLoader() {
        this.loader?.hide();
    }
    /**
     * Avanzamento dell'anello durante il waterfall del pre-roll (il loader e' visibile solo li').
     * A ogni VAST l'anello va piano verso la fine del suo tratto: se il VAST risponde subito
     * completeLoaderProgress lo chiude, se fallisce il VAST successivo riparte da li'.
     */
    updateLoaderProgress() {
        if (this.rollType !== ADS_ROLL_TYPE.PRE_ROLL) {
            return;
        }
        this.loader?.setProgress(prerollStepTarget(this.waterfallIndex - 1, this.waterfall.length), LOADER_CREEP_MS);
    }
    completeLoaderProgress() {
        if (this.rollType === ADS_ROLL_TYPE.PRE_ROLL) {
            this.loader?.setProgress(1, LOADER_DONE_MS);
        }
    }
}
